// Metabase's raw result set, and the by-name column reading every parse of it shares.
//
// Split out of `metricResult.ts` (PLA-8828) because ONE endpoint answers with several unrelated
// column schemas: the standard metric row, Product Readiness (`*-PR-*`, 25 columns and no
// `metric_value` at all) and three bespoke Reversal shapes. `MetricResultRow` cannot describe those
// -- its `calculation_status` and `metric_value` are not columns they carry -- so each gets its own
// typed parse, and they share these primitives rather than three copies of the same `findIndex`.
// Contract PLA-8825.

export interface MetabaseColumn {
  name: string;
  [key: string]: unknown;
}

// What `GET /v4/dash/customer_intelligence/metrics/{metric_id}` returns. perx-api is a proxy: it
// resolves the tenant, timezone and ACL server-side into a signed JWT and then forwards Metabase's
// result set VERBATIM, so everything under `payload` is Metabase's own shape and not a perx schema.
// Contract PLA-8817.
export interface MetricEnvelope {
  metric_id: string;
  payload: {
    // Metabase answers HTTP 202 for a failed query as readily as a successful one, so this is the
    // only discriminator. perx-api turns a non-`completed` body into a 502, which means the
    // dashboard should never see one — checked anyway, because "should never" is not a guarantee.
    status?: string;
    data?: {
      rows?: unknown[][];
      cols?: MetabaseColumn[];
    };
  };
}

export const COMPLETED_STATUS = 'completed';

export const asNumber = (cell: unknown): number | null => (typeof cell === 'number' ? cell : null);

export const asString = (cell: unknown): string | null => (typeof cell === 'string' ? cell : null);

/**
 * VERIFIED 2026-09-09: the `O-*-PR-*` rows carry real JSON booleans in `confidence_cap_applied` and
 * `handoff_confidence_ok`, so no widening is needed. Only a real `boolean` is accepted — a `1`/`0`
 * or `"true"` would read as `null` (unknown), never as a substituted `false`. PLA-8828, PLA-8815.
 */
export const asBoolean = (cell: unknown): boolean | null =>
  typeof cell === 'boolean' ? cell : null;

export interface MetabaseTable {
  cols: MetabaseColumn[];
  rows: unknown[][];
}

export const tableFrom = (envelope: MetricEnvelope | undefined | null): MetabaseTable => ({
  cols: envelope?.payload?.data?.cols ?? [],
  rows: envelope?.payload?.data?.rows ?? []
});

export interface ColumnReader {
  /** Whether this metric's question returns the column at all — absent is not the same as null. */
  has: (name: string) => boolean;
  raw: (row: unknown[], name: string) => unknown;
  text: (row: unknown[], name: string) => string | null;
  number: (row: unknown[], name: string) => number | null;
  boolean: (row: unknown[], name: string) => boolean | null;
}

/**
 * Resolves cells by column NAME, never by position, and indexes `cols` once per envelope.
 *
 * The contract states the column set is whatever each metric's own Metabase question defines, so a
 * metric may return more columns, fewer, or the same ones in a different order — a positional read
 * would silently transpose two fields the first time a question is edited. First occurrence wins on
 * a duplicated name, matching the `findIndex` this replaces.
 */
// Redshift names columns in lower case and Snowflake in upper case, so names match either way.
const columnKey = (name: unknown): string | null =>
  typeof name === 'string' && name !== '' ? name.toLowerCase() : null;

/** The position of a column by name, or -1. */
export const columnIndexOf = (cols: MetabaseColumn[], name: string): number =>
  cols.findIndex(col => Boolean(col) && columnKey(col.name) === columnKey(name));

export const readerFor = (cols: MetabaseColumn[]): ColumnReader => {
  const index: Record<string, number> = {};

  cols.forEach((col, at) => {
    const key = col ? columnKey(col.name) : null;

    if (key !== null && !(key in index)) {
      index[key] = at;
    }
  });

  const at = (name: string): number | undefined => {
    const key = columnKey(name);

    return key !== null && key in index ? index[key] : undefined;
  };

  const raw = (row: unknown[], name: string): unknown => {
    const position = at(name);

    return position === undefined ? undefined : row[position];
  };

  return {
    has: name => at(name) !== undefined,
    raw,
    text: (row, name) => asString(raw(row, name)),
    number: (row, name) => asNumber(raw(row, name)),
    boolean: (row, name) => asBoolean(raw(row, name))
  };
};
